import { useNavigate } from '@tanstack/react-router'
import { LibraDashboardConfig } from 'features/libra/dashboard/libraDashboardApi'
import { LibraDashboardGroupSelect } from 'features/libra/dashboard/LibraDashboardGroupSelect'
import { useLibraGroups } from 'features/libra/ticket/libraTicketsApi'
import { type FC, useCallback, useMemo } from 'react'
import { SEARCH_PARAM_UNIT_ID } from 'routes/(common_search_params)/-searchParams'
import { SEARCH_PARAM_ORG_ID } from 'routes/(global_search_params)/-searchParams'
import {
  SEARCH_PARAM_DASHBOARD_CONFIG_ID,
  SEARCH_PARAM_GROUP,
} from 'routes/libra-dashboard/-searchParams'
import styled from 'styled-components'

import { LibraDashboardContent } from './contentView/LibraDashboardContent'

type LibraDashboardViewProps = {
  config: LibraDashboardConfig
  groupId?: string
  unitId?: string
  orgId?: string
  dashboardConfigId?: string
}

export const LibraDashboardView: FC<LibraDashboardViewProps> = ({
  config,
  groupId,
  unitId,
  orgId,
  dashboardConfigId,
}) => {
  const { title, tags } = config
  const navigate = useNavigate()

  // Query all groups for the organization (not filtered by dashboard config)
  const { groups: libraGroups, forceRefresh: forceRefreshLibraGroups } =
    useLibraGroups({
      tags,
    })

  const organizationId = tags?.organization as string | undefined

  const selectedGroup = useMemo(
    () => libraGroups.find((g) => g.id === groupId),
    [libraGroups, groupId]
  )

  const handleGroupSelect = useCallback(
    (newGroupId: string | undefined) => {
      if (orgId && dashboardConfigId) {
        navigate({
          to: '/libra-dashboard',
          search: {
            [SEARCH_PARAM_ORG_ID]: orgId,
            [SEARCH_PARAM_DASHBOARD_CONFIG_ID]: dashboardConfigId,
            [SEARCH_PARAM_GROUP]: newGroupId,
            [SEARCH_PARAM_UNIT_ID]: unitId,
          },
        })
      }
    },
    [dashboardConfigId, navigate, orgId, unitId]
  )

  return (
    <ViewContainer>
      <Heading>{title}</Heading>
      <BodyLayout>
        <GroupColumn>
          <LibraDashboardGroupSelect
            groups={libraGroups}
            selectedGroupId={groupId}
            onGroupSelect={handleGroupSelect}
            organizationId={organizationId}
            onGroupCreated={forceRefreshLibraGroups}
          />
        </GroupColumn>
        <DetailColumn>
          {selectedGroup ? (
            <LibraDashboardContent
              config={config}
              group={selectedGroup}
              unitId={unitId}
              orgId={orgId}
              dashboardConfigId={dashboardConfigId}
              onRefreshGroup={forceRefreshLibraGroups}
            />
          ) : (
            <DetailSplash>Select a Group</DetailSplash>
          )}
        </DetailColumn>
      </BodyLayout>
    </ViewContainer>
  )
}

const ViewContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.75rem 1.5rem 1.5rem 1.5rem;
  max-width: 100%;
`

const Heading = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.forecast.activeText};
`

const BodyLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  min-height: 0;
  flex: 1 1 auto;
`

const GroupColumn = styled.div`
  width: 20%;
  min-width: 12rem;
  max-width: 16rem;
`

const DetailColumn = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const DetailSplash = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
`
